import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { EditFieldButton } from "@/shared/components/EditFieldButton";
import { UpdateActionRequest } from "@/shared/domain/actions/ActionRecord";

import { ActionMarkdown } from "./ActionMarkdown";

interface EditableListItemProps {
  title: string;
  field: string;
  value: string | undefined;
  onChange: (updates: UpdateActionRequest) => void;
  setErrorText: (arg: string) => void;
  renderMarkdown?: boolean;
  disablePadding?: boolean;
  onHelperClick?: () => void;
  canEdit?: boolean;
  maxLength?: number;
}

export function EditableListItem({
  title,
  field,
  value,
  onChange,
  setErrorText,
  renderMarkdown,
  disablePadding,
  onHelperClick,
  canEdit = false,
  maxLength,
}: EditableListItemProps) {
  const theme = useTheme();
  const [showEditableIcon, setShowEditableIcon] = React.useState(false);
  const [editable, setEditable] = React.useState(false);
  const [currentValue, setCurrentValue] = React.useState<string | null>(
    value || null,
  );

  const updateAction = () => {
    setErrorText("");

    onChange({
      [field]: currentValue,
    });
  };

  const onClickEditSave = () => {
    if (editable) {
      updateAction();
    }
    setEditable(!editable);
  };

  let actionMarkdownContent;

  if (currentValue) {
    if (renderMarkdown) {
      actionMarkdownContent = <ActionMarkdown text={currentValue} />;
    } else {
      actionMarkdownContent = currentValue;
    }
  } else {
    actionMarkdownContent = "None";
  }

  return (
    <ListItem
      onMouseOver={() => {
        if (canEdit) {
          setShowEditableIcon(true);
        }
      }}
      onMouseLeave={() => {
        setShowEditableIcon(false);
      }}
      disablePadding={disablePadding}
    >
      <ListItemText
        primaryTypographyProps={{
          component: "span",
        }}
        sx={{
          mt: disablePadding ? 0 : theme.spacing(0.5),
        }}
        primary={
          <>
            <span>{title}</span>

            {onHelperClick && (
              <IconButton
                sx={{
                  p: "5px",
                  verticalAlign: "middle",
                  ml: theme.spacing(0.25),
                }}
                onClick={onHelperClick}
              >
                <InfoIcon
                  color="primary"
                  sx={{
                    fontSize: "1.1rem",
                    cursor: "pointer",
                  }}
                />
              </IconButton>
            )}

            <EditFieldButton
              editable={editable}
              showEditableIcon={showEditableIcon}
              onClick={onClickEditSave}
            />

            <Box>
              {editable === false ? (
                <Typography
                  component="span"
                  variant="body2"
                  style={{
                    whiteSpace: "pre-wrap",
                    color: theme.palette.text.secondary,
                    wordBreak: "break-word",
                  }}
                >
                  {actionMarkdownContent}
                </Typography>
              ) : (
                <TextField
                  sx={{ mt: theme.spacing(1) }}
                  autoFocus
                  fullWidth
                  multiline
                  size="small"
                  value={currentValue}
                  variant="outlined"
                  inputProps={{
                    style: {
                      fontSize: "0.875rem",
                    },
                    maxLength: maxLength,
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setCurrentValue(event.target.value);
                  }}
                />
              )}
            </Box>
          </>
        }
      />
    </ListItem>
  );
}
